import React, { useEffect, useState } from 'react';
import { Button } from '../ui/button';
import { apiService, ApiError } from '../../services/api';
import { formatCurrency, formatDateTime } from '../../lib/utils';
import { Loader2, Search, MoreVertical } from 'lucide-react';
import { AddTradelineDialog } from './AddTradelineDialog';
import { TradelineOrder, TradelineOrderStatus } from '../../types/tradeline-orders';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../ui/dropdown-menu';
import { useConvexAuth, useQuery } from 'convex/react';
import { api } from '../../convex/api';

export function TradelinesManager() {
  const [ordersError, setOrdersError] = useState('');
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [extendingOrderId, setExtendingOrderId] = useState<string | null>(null);
  const [expandedOrderId, setExpandedOrderId] = useState<string | null>(null);
  const { isAuthenticated, isLoading: isAuthLoading } = useConvexAuth();
  const ordersQuery = useQuery(
    api.tradeline_orders.listForAuthenticatedUser,
    isAuthenticated
      ? {
          status: undefined,
          start_date: undefined,
          end_date: undefined,
        }
      : 'skip'
  ) as TradelineOrder[] | null | undefined;
  const orders = ordersQuery ?? [];
  const isLoadingOrders = isAuthLoading || (isAuthenticated && ordersQuery === undefined);
  const isRealtimeConnecting = !isAuthLoading && !isAuthenticated;
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);

  useEffect(() => {
    if (!isLoadingOrders && ordersQuery !== undefined) {
      setHasLoadedOnce(true);
    }
  }, [isLoadingOrders, ordersQuery]);

  useEffect(() => {
    console.info('[TradelinesRealtime] Connection state', {
      isAuthLoading,
      isAuthenticated,
      isRealtimeConnecting,
      ordersQueryState:
        ordersQuery === undefined
          ? 'undefined'
          : ordersQuery === null
            ? 'null'
            : `rows:${ordersQuery.length}`,
    });
  }, [isAuthLoading, isAuthenticated, isRealtimeConnecting, ordersQuery]);

  const handleExtendNow = async (orderId: string) => {
    try {
      setExtendingOrderId(orderId);
      setOrdersError('');
      const response = await apiService.extendTradelineOrder(orderId);
      const checkoutUrl = response?.data?.checkout_url;
      if (checkoutUrl) {
        window.open(checkoutUrl, '_blank');
      }
    } catch (err) {
      setOrdersError(err instanceof ApiError ? err.message : 'Unable to extend tradeline order. Please try again.');
    } finally {
      setExtendingOrderId(null);
    }
  };

  const handleCompletePayment = (order: TradelineOrder) => {
    const checkoutUrl = order.stripe_checkout_url || order.payment_url;
    if (checkoutUrl) {
      window.open(checkoutUrl, '_blank');
      return;
    }
    setOrdersError('Checkout URL is unavailable for this order.');
  };

  const getStatusBadgeClass = (status: TradelineOrderStatus) => {
    switch (status) {
      case 'waiting_payment':
        return 'text-yellow-800 border-yellow-200';
      case 'payment_received':
        return 'text-blue-800 border-blue-200';
      case 'lines_ordered':
        return 'text-purple-800 border-purple-200';
      case 'lines_added':
        return 'text-orange-800 border-orange-200';
      case 'lines_posted':
        return 'text-green-800 border-green-200';
      case 'expired':
        return 'text-gray-800 border-gray-200';
      case 'cancelled':
        return 'text-red-800 border-red-200';
      case 'refunded':
        return 'text-pink-800 border-pink-200';
      default:
        return 'text-gray-800 border-gray-200';
    }
  };

  const formatStatus = (status: TradelineOrderStatus) =>
    status.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold">Tradelines</h2>
        <Button
          onClick={() => setIsAddDialogOpen(true)}
          className="h-9 px-4 bg-black hover:bg-gray-800 text-white"
          style={{ borderRadius: '8px' }}
        >
          <span className="text-lg mr-2">+</span>
          Add New
        </Button>
      </div>

      {/* Error states */}
      {!isLoadingOrders && !isAuthLoading && !isAuthenticated && !isRealtimeConnecting && (
        <div className="p-6">
          <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-sm text-red-600">Unable to initialize realtime tradeline orders.</p>
          </div>
        </div>
      )}
      {ordersError && (
        <div className="p-6">
          <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-sm text-red-600">{ordersError}</p>
          </div>
        </div>
      )}

      {/* First-time loading (no data yet) */}
      {(isLoadingOrders || isRealtimeConnecting) && !hasLoadedOnce ? (
        <div className="bg-white border border-gray-200 rounded-lg">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="border-b border-gray-200">
                <tr>
                  <th className="px-6 py-2 text-left text-sm font-medium text-gray-500">Order ID</th>
                  <th className="px-6 py-2 text-left text-sm font-medium text-gray-500">Items</th>
                  <th className="px-6 py-2 text-left text-sm font-medium text-gray-500">Total</th>
                  <th className="px-6 py-2 text-left text-sm font-medium text-gray-500">Created</th>
                  <th className="px-6 py-2 text-left text-sm font-medium text-gray-500">Status</th>
                  <th className="px-6 py-2 text-right text-sm font-medium text-gray-500">Actions</th>
                </tr>
              </thead>
            </table>
          </div>
          <div className="flex items-center justify-center py-16">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        </div>
      ) : orders.length === 0 && !isLoadingOrders ? (
        <div
          className="bg-white border border-gray-200 rounded-lg"
          style={{ animation: 'fadeInUp 0.5s ease-out' }}
        >
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="border-b border-gray-200">
                <tr>
                  <th className="px-6 py-2 text-left text-sm font-medium text-gray-500">Order ID</th>
                  <th className="px-6 py-2 text-left text-sm font-medium text-gray-500">Items</th>
                  <th className="px-6 py-2 text-left text-sm font-medium text-gray-500">Total</th>
                  <th className="px-6 py-2 text-left text-sm font-medium text-gray-500">Created</th>
                  <th className="px-6 py-2 text-left text-sm font-medium text-gray-500">Status</th>
                  <th className="px-6 py-2 text-right text-sm font-medium text-gray-500">Actions</th>
                </tr>
              </thead>
            </table>
          </div>
          <div className="text-center py-16">
            <div className="flex items-center justify-center mx-auto mb-4 bg-gray-100" style={{ width: '40px', height: '40px', borderRadius: '8px' }}>
              <Search className="h-5 w-5 text-gray-400" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">No Orders Yet</h3>
            <p className="text-sm text-gray-500">
              You have no tradeline orders yet. Click<br />"Add New" to get recommendations and checkout.
            </p>
          </div>
        </div>
      ) : (orders.length > 0 || isLoadingOrders) ? (
        <div className="relative">
          {/* Loading overlay -- shown over previous data while refreshing */}
          {isLoadingOrders && hasLoadedOnce && (
            <div className="absolute inset-0 bg-background/80 backdrop-blur-sm z-10 flex items-center justify-center min-h-[200px] transition-opacity duration-200 rounded-lg">
              <div className="text-center space-y-3">
                <Loader2 className="h-6 w-6 animate-spin mx-auto text-primary" />
                <p className="text-sm text-muted-foreground">Loading orders...</p>
              </div>
            </div>
          )}
          <div
            className={`transition-all duration-500 ease-out ${
              isLoadingOrders && hasLoadedOnce
                ? 'opacity-50 translate-y-0.5'
                : 'opacity-100 translate-y-0'
            }`}
            style={{
              animation: isLoadingOrders ? 'none' : 'fadeInUp 0.5s ease-out'
            }}
          >
        <div className="bg-white border border-gray-200 rounded-lg">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="border-b border-gray-200">
                <tr>
                  <th className="px-6 py-2 text-left text-sm font-medium text-gray-500">Order ID</th>
                  <th className="px-6 py-2 text-left text-sm font-medium text-gray-500">Items</th>
                  <th className="px-6 py-2 text-left text-sm font-medium text-gray-500">Total</th>
                  <th className="px-6 py-2 text-left text-sm font-medium text-gray-500">Created</th>
                  <th className="px-6 py-2 text-left text-sm font-medium text-gray-500">Status</th>
                  <th className="px-6 py-2 text-right text-sm font-medium text-gray-500">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {orders.map((order) => {
                  const itemCount = order.items?.reduce((sum, item) => sum + item.quantity, 0) || 0;
                  const isExpanded = expandedOrderId === order._id;
                  return (
                    <React.Fragment key={order._id}>
                      <tr
                        className="hover:bg-gray-50 transition-colors cursor-pointer"
                        onClick={() => setExpandedOrderId(isExpanded ? null : order._id)}
                      >
                        <td className="px-6 py-3">
                          <div className="text-sm text-gray-900 font-mono">#{order._id.slice(0, 8)}</div>
                        </td>
                        <td className="px-6 py-3">
                          <div className="text-sm text-gray-900">{itemCount}</div>
                        </td>
                        <td className="px-6 py-3">
                          <div className="text-sm text-gray-900">{formatCurrency(order.total_amount)}</div>
                        </td>
                        <td className="px-6 py-3">
                          <div className="text-sm text-gray-500">{formatDateTime(order.created_at)}</div>
                        </td>
                        <td className="px-6 py-3">
                          <span className={`inline-flex items-center px-2.5 py-1 rounded-md text-xs border ${getStatusBadgeClass(order.status)}`}>
                            {formatStatus(order.status)}
                          </span>
                        </td>
                        <td className="px-6 py-3">
                          <div className="flex justify-end">
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className="h-8 w-8 p-0"
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  <MoreVertical className="h-4 w-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end" className="w-44">
                                {order.status === 'waiting_payment' && (
                                  <DropdownMenuItem
                                    onClick={() => handleCompletePayment(order)}
                                  >
                                    Complete Payment
                                  </DropdownMenuItem>
                                )}
                                <DropdownMenuItem
                                  onClick={() => handleExtendNow(order._id)}
                                  className={extendingOrderId === order._id ? 'opacity-50 cursor-not-allowed' : ''}
                                >
                                  {extendingOrderId === order._id ? (
                                    <>
                                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                      Extending...
                                    </>
                                  ) : (
                                    'Extend Now'
                                  )}
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </div>
                        </td>
                      </tr>
                      {isExpanded && (
                        <tr className="bg-gray-50">
                          <td colSpan={6} className="px-6 py-4">
                            <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                              <table className="w-full table-fixed">
                                <thead className="border-b border-gray-200">
                                  <tr>
                                    <th className="px-4 py-2 text-left text-sm font-medium text-gray-500 w-[22%]">Tradeline</th>
                                    <th className="px-4 py-2 text-left text-sm font-medium text-gray-500">Age</th>
                                    <th className="px-4 py-2 text-left text-sm font-medium text-gray-500">Credit Limit</th>
                                    <th className="px-4 py-2 text-left text-sm font-medium text-gray-500 w-[20%]">Provider</th>
                                    <th className="px-4 py-2 text-left text-sm font-medium text-gray-500">Price</th>
                                    <th className="px-4 py-2 text-left text-sm font-medium text-gray-500">Qty</th>
                                    <th className="px-4 py-2 text-left text-sm font-medium text-gray-500">Total</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {order.items && order.items.length > 0 ? (
                                    <>
                                      {order.items.map((item, idx) => (
                                        <tr key={`${order._id}-${item.tradeline_inventory_id}-${idx}`} className="border-b border-gray-200 last:border-b-0">
                                          <td className="px-4 py-2 w-[22%]">
                                            <div className="text-sm text-gray-900">{item.name}</div>
                                            <div
                                              className="text-xs text-gray-500 max-w-[220px] truncate"
                                              title={item.tradeline_inventory_id}
                                            >
                                              {item.tradeline_inventory_id}
                                            </div>
                                          </td>
                                          <td className="px-4 py-2 text-sm text-gray-700">{item.age_years.toFixed(2)} years</td>
                                          <td className="px-4 py-2 text-sm text-gray-700">{formatCurrency(item.credit_limit)}</td>
                                          <td className="px-4 py-2 text-sm text-gray-700 w-[20%]">{item.provider}</td>
                                          <td className="px-4 py-2 text-sm text-gray-700">{formatCurrency(item.price)}</td>
                                          <td className="px-4 py-2 text-sm text-gray-700">{item.quantity}</td>
                                          <td className="px-4 py-2 text-sm font-medium text-gray-900">
                                            {formatCurrency(item.price * item.quantity)}
                                          </td>
                                        </tr>
                                      ))}
                                    </>
                                  ) : (
                                    <tr>
                                      <td colSpan={7} className="px-4 py-4 text-sm text-gray-500 text-center">
                                        No items found for this order.
                                      </td>
                                    </tr>
                                  )}
                                </tbody>
                              </table>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
          </div>
        </div>
      ) : null}

      <AddTradelineDialog
        isOpen={isAddDialogOpen}
        onOpenChange={setIsAddDialogOpen}
        onOrderCreated={() => {}}
      />
    </div>
  );
}
